import { associateCommands } from "./services/addin-commands";

const HOST_FLAG = "diceus-office-host";
const ADDIN_PATH = "/excel-addin";

/**
 * Excel expects the page it loaded to answer Office.onReady shortly after it starts, and gives up with
 * "this add-in may not load properly" if it does not. The auth guard can send the first navigation to the
 * sign-in page long before the task pane component exists, so the handshake cannot wait for routing — it
 * happens here, before the router runs, on every page of the session that started inside Excel.
 */
export function isOfficeHost(): boolean {
  if (window.location.pathname.startsWith(ADDIN_PATH)) {
    try {
      sessionStorage.setItem(HOST_FLAG, "1");
    } catch {
      return true;
    }

    return true;
  }

  try {
    return sessionStorage.getItem(HOST_FLAG) === "1";
  } catch {
    return false;
  }
}

type TNullableHistory = History & {
  pushState: History["pushState"] | null;
  replaceState: History["replaceState"] | null;
};

export function allowRouting(): void {
  if (!isOfficeHost()) {
    return;
  }

  const history = window.history as TNullableHistory;

  if (history.pushState === null) {
    history.pushState = () => undefined;
  }

  if (history.replaceState === null) {
    history.replaceState = () => undefined;
  }
}

const HANDSHAKE_TIMEOUT_MS = 5000;

type TOfficeReady = Awaited<ReturnType<typeof Office.onReady>>;

let handshake: Promise<TOfficeReady | null> | null = null;

// Bounded on purpose: an initializer that never settles leaves the whole application unbootstrapped,
// and a blank pane is a worse failure than one that loads without the host.
export function whenOfficeReady(): Promise<TOfficeReady | null> {
  if (!isOfficeHost() || typeof Office === "undefined") {
    return Promise.resolve(null);
  }

  handshake ??= Promise.race([
    Office.onReady().catch(() => null),
    new Promise<null>((resolve) => setTimeout(() => resolve(null), HANDSHAKE_TIMEOUT_MS)),
  ]);

  return handshake;
}

export async function isExcelHost(): Promise<boolean> {
  const info = await whenOfficeReady();

  return info !== null && info.host === Office.HostType.Excel;
}

export function initOfficeHost(): () => Promise<void> {
  return async () => {
    await whenOfficeReady();
    associateCommands();
  };
}

